import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, unref as _unref } from "vue"

const _hoisted_1 = { class: "potree_container" }
const _hoisted_2 = {
  key: 1,
  class: "d-flex justify-content-center align-items-center empty-project"
}
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", null, "No data for current project/survey combination!", -1)
const _hoisted_4 = [
  _hoisted_3
]
const _hoisted_5 = /*#__PURE__*/_createElementVNode("div", { class: "coordinates" }, null, -1)

import { LayerItem } from "@/classes/data-structures/layer-item";
import { Tools } from "@/classes/viewer/tools";
import { nextTick, PropType, watch, reactive, onMounted, ref, onBeforeUnmount, toRaw } from "vue";
import { CustomViewer } from "@/classes/viewer/custom-viewer";
import type { ExternalImage } from "@/classes/viewer/external-image";
import { LayerStatus } from "@/classes/data-structures/layer-status";
import { ViewType } from "@/classes/data-structures/view-type";
import { LayerItemType } from "@/classes/data-structures/layer-item-type";
import { useAppStateStore } from "@/store/app-state";
import { useDataStore } from "@/store/data-store";
import { ViewIndex } from "@/classes/data-structures/view-index";
import { OrthophotoMetadata, Survey, SurveyInputData, SurveyInputDataStatusEnum, SurveyInputDataTypeEnum, VolumeMetadata } from "@/swagger";
import Loading from "../Pages/Loading.vue";
import MapboxAttribution from "../Other/MapboxAttribution.vue";
import ExternalImageControls from "./ExternalImageControls.vue";

interface PotreeViewerData {
	ViewType: any,
	wasCreated: boolean,
	viewer?: CustomViewer,
	hasNoData: boolean,
	isLoading: boolean,
	externalViewerImage: ExternalImage | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PotreeViewer',
  props: {
	viewIndex: {
		type: String as PropType<ViewIndex>,
		required: true,
		default: ViewIndex.View1
	},
	fadeInput: {
		type: Boolean,
		default: true
	},
	pointBudgetMultiplier: {
		type: Number,
		default: 1
	},
	defaultZoomFactor: {
		type: Number
	},
	showUi: {
		type: Boolean,
		default: true
	},
	hideVolumesAndProfiles: {
		type: Boolean,
		default: false
	}
},
  emits: ["external-image-viewer-open-changed"],
  setup(__props, { expose, emit }: { emit: ({ (event: "external-image-viewer-open-changed", open: boolean): void }), expose: any, slots: any, attrs: any }) {

const props = __props;




const data = reactive<PotreeViewerData>({
	ViewType: ViewType,
	wasCreated: false,
	hasNoData: false,
	isLoading: false,
	externalViewerImage: null,
});

const appState = useAppStateStore();
const dataStore = useDataStore();
const potreeRenderArea = ref(null)
let viewer: CustomViewer;
let loadQueue: any[] = [];
let loadedSurveyUUID: string | undefined = undefined;
let layers: LayerItem[] = [];
let intervalId: number | undefined;

function isProfileInputFile(file: SurveyInputData): boolean {
	if (!file) {
		return false;
	}
	const typeValue = typeof file.type === "string" ? file.type.toLowerCase() : "";
	if (typeValue === "profile") {
		return true;
	}
	const originValue = typeof file.uploadOrigin === "string" ? file.uploadOrigin.toLowerCase() : "";
	return originValue === "profile";
}

function copyViewFromViewer(trackedViewer: any) {
	if (trackedViewer) {
		let viewer2 = (trackedViewer as any).getViewer();
		if (viewer2) {
			viewer.scene.view = viewer2.scene.view;
		}
	}
}

function onToolCancel() {
	appState.setSelectedTool(Tools.Selection);
}

function onSelectedElementChanged(element) {
	appState.setSelectedElement(element);
}

function openExternalImage(image: ExternalImage) {
	data.externalViewerImage = image;
	emit("external-image-viewer-open-changed", true);
}

function closeExternalImage() {
	data.externalViewerImage = null;
	emit("external-image-viewer-open-changed", false);
}

function zoomToFit() {
	viewer?.zoomToFit();
}

function zoomIn() {
	viewer?.zoomIn();
}

function zoomOut() {
	viewer?.zoomOut();
}

function onMapTypeChanged() {
	const mapType = appState.mapType;
	viewer?.setMapType(mapType);
}

let previousFileStatuses = new Map<string, string>();
let isReloading = false;

function startInterval() {
    if (!intervalId) {
        // Initialize the status map with current statuses
        appState.view1Survey?.inputFiles?.forEach(file => {
            previousFileStatuses.set(file.uuid, file.status);
        });

        intervalId = setInterval(async () => {
            // Skip if already reloading or currently loading
            if (isReloading || data.isLoading) {
                console.log("Skipping reload check - already loading");
                return;
            }

            const currentProjectUuid = appState.project?.uuid;
            const currentSurveyUuid = appState.view1Survey?.uuid;

            if (!currentProjectUuid || !currentSurveyUuid) {
                stopInterval();
                return;
            }

            // Fetch fresh project data (without triggering a reload)
            const freshProjectData = await dataStore.getProject(currentProjectUuid);
            if (!freshProjectData) {
                return;
            }

            // Find the current survey in the fresh data
            const freshSurvey = freshProjectData.surveys?.find(s => s.uuid === currentSurveyUuid);
            if (!freshSurvey?.inputFiles) {
                return;
            }

            const hasProjectsInProcessing = freshSurvey.inputFiles.some(file =>
                file.status === "processing" || file.status === "waitingforprocessing"
            );

            // Check each file to see if it changed from processing to processed
            let hasStatusChange = false;
            freshSurvey.inputFiles.forEach(file => {
                const previousStatus = previousFileStatuses.get(file.uuid);
                if ((previousStatus === "processing" || previousStatus === "waitingforprocessing") &&
                    file.status === "processed") {
                    console.log(`File ${file.name} changed from ${previousStatus} to processed`);
                    hasStatusChange = true;
                }
                // Update the status map
                previousFileStatuses.set(file.uuid, file.status);
            });

            // Only reload if a file status actually changed
            if (hasStatusChange) {
                console.log("Refreshing surveys due to file status change");
                isReloading = true;
                try {
                    await appState.reloadCurrentProject();
                } finally {
                    isReloading = false;
                }
            }

            if (!hasProjectsInProcessing) {
                console.log("All projects done processing, stopping interval");
                previousFileStatuses.clear();
                stopInterval();
            }
        }, 5000);
    }
}

function stopInterval() {
    if (intervalId) {
        clearInterval(intervalId);
        intervalId = undefined;
    }
}

async function onSurveyChanged() {
	const survey = props.viewIndex == ViewIndex.View1 ? appState.view1Survey : appState.view2Survey;
	console.log(`Survey changed for view ${props.viewIndex}`, survey);

	loadQueue.push(async () => {
		// if (survey && survey.uuid === loadedSurveyUUID) {
		// 	console.log("Trying to load same survey twice. stopping...");
		// 	return;
		// }
		//wait for current survey to load before 
		data.isLoading = true;
		viewer.cleanup();
		closeExternalImage();
		data.hasNoData = false;
		if (survey) {
			await loadSurvey(survey);
		} else {
			data.hasNoData = true;
		}
		data.isLoading = false;
		processLoadingQueue();
	});

	processLoadingQueue();
}

function processLoadingQueue() {
	if (!data.isLoading && loadQueue.length > 0) {
		//Take the last element and clear the queue
		let last = loadQueue[loadQueue.length - 1];
		loadQueue.splice(0, loadQueue.length);
		last();
	}
}

async function loadSurvey(survey: Survey): Promise<boolean> {
	if (survey && survey.pointclouds && survey.cad && survey.orthophotos && survey.models && survey.profiles && survey.volumes) {
		// console.log("loadSurvey() start", survey.uuid);
		loadedSurveyUUID = survey.uuid;
		//Load survey data
		const surveyInputFiles = survey.inputFiles ?? [];
		const externalImages = survey.externalImages ?? [];
		const has3dData = survey.pointclouds.length > 0 || survey.models.length > 0 || externalImages.length > 0;
		const hasOrthophotoData = survey.orthophotos.length > 0;
		const hasCadData = survey.cad.length > 0;
		const hasPointclouds = survey.pointclouds.length > 0;
		const pendingProfiles = surveyInputFiles.filter((file) => isProfileInputFile(file) && file.status !== SurveyInputDataStatusEnum.Processed);
		const hasProfiles = !props.hideVolumesAndProfiles && (survey.profiles.length > 0 || pendingProfiles.length > 0);
		const hasVolumes = !props.hideVolumesAndProfiles && (survey.volumes && survey.volumes.length > 0);

		// Check for pending files to enable view switching even before processing completes
		const hasPending3dData = surveyInputFiles.some(file =>
			(file.type === SurveyInputDataTypeEnum.Model || file.type === SurveyInputDataTypeEnum.Pointcloud) &&
			(file.status === SurveyInputDataStatusEnum.Processing || file.status === SurveyInputDataStatusEnum.Waitingforprocessing)
		);
		const hasPendingOrthophotoData = surveyInputFiles.some(file =>
			file.type === SurveyInputDataTypeEnum.Orthophoto &&
			(file.status === SurveyInputDataStatusEnum.Processing || file.status === SurveyInputDataStatusEnum.Waitingforprocessing)
		);

		const promises: Promise<any>[] = [];

		createLayers(survey);

		viewer.setProjectWKTCoordinateSystem(appState.project?.coordinateSystem?.wktString);

		for (const item of survey.pointclouds) {
			const p = viewer.loadPointcloud(item.resourceUrl, item.name, item.uuid);
			p.then(()=>{
				var layer = layers.find((l)=>l.id == item.uuid);
				if(layer){
					layer.layerStatus = LayerStatus.ProcessedAndLoaded;
				}
			});
			promises.push(p);
		}

		for (const item of survey.models) {
			let modelUrl = item.resourceUrl;
			let transformationUrl;
			if (item.files.length > 1) {
				transformationUrl = item.files.find(file => file.endsWith(".js"));
			}
			const p = viewer.loadModel(modelUrl, item.name, item.uuid, !hasPointclouds, transformationUrl);
			p.then(()=>{
				var layer = layers.find((l)=>l.id == item.uuid);
				if(layer){
					layer.layerStatus = LayerStatus.ProcessedAndLoaded;
				}
			});
			promises.push(p);
		}

		for (const item of survey.orthophotos) {
			let additionalData = item.additionalData as OrthophotoMetadata;
			viewer.loadOrthophoto(item.resourceUrl, item.name, additionalData.bounds, additionalData.minZoom, additionalData.maxZoom, item.uuid);
		}

		for (const item of survey.cad) {
			const p = viewer.loadDXF(item.resourceUrl);
			promises.push(p);
		}

		for (const item of externalImages) {
			viewer.externalImageManager.load(item);
		}

		if (!props.hideVolumesAndProfiles) {
			for (const item of survey.profiles) {
				const p = viewer.loadProfile(item.resourceUrl);
				promises.push(p);
			}
			for (const item of survey.volumes || []) {
				let additionalData = item.additionalData as VolumeMetadata;			
				const p = viewer.loadVolume(item.resourceUrl, item.name, item.uuid, true, item.files, toRaw(additionalData));
				p.then(()=>{
					const layer = layers.find((l)=>l.id == item.uuid);
					if(layer){
						layer.layerStatus = LayerStatus.ProcessedAndLoaded;
					}
				});
				promises.push(p);
			}
		}

		// console.log("loadSurvey() end", survey.uuid);

		//Set weather 2D or 3D view is available or both
		const availableViewTypes: ViewType[] = [];
		if (has3dData || hasCadData || hasProfiles || hasVolumes || hasPending3dData) {
			availableViewTypes.push(ViewType.View3D);
		}
		if (hasOrthophotoData || hasProfiles || hasVolumes || hasPendingOrthophotoData) {
			availableViewTypes.push(ViewType.View2D);
		}
		appState.setAvailableViewTypes(availableViewTypes);

		let activeTab = LayerItemType.Meshes;
		//Switch between map or 3d view depending on which data we have
		if (has3dData) {
			appState.setViewType(ViewType.View3D);
			if (hasPointclouds) {
				activeTab = LayerItemType.PointClouds;
			}
		} else if (hasOrthophotoData) {
			appState.setViewType(ViewType.View2D);
			activeTab = LayerItemType.Orthophoto;
		} else if(hasCadData) {
			appState.setViewType(ViewType.View3D);
			activeTab = LayerItemType.Cad;
		} else {
			//No data... display some kind of message
		}

		//Check if we have orthophoto data but dont have CSR in that case we should not show
		//Button for changing map type between satellite and map
		appState.canChangeMapType = appState.project?.coordinateSystem?.wktString !== undefined;

		//Check if split view can be used (only available when multiple project surveys exist)
		appState.canCompareProject = appState.project.surveys.length > 1;


		//Set active tab in sidebar
		appState.setViewTab(props.viewIndex, activeTab);

		data.hasNoData = !hasOrthophotoData && !has3dData && !hasPointclouds && !hasCadData && !hasProfiles && !hasVolumes;

		if (availableViewTypes.includes(appState.viewType)) {
			viewer.changeViewType(appState.viewType);
		} else if (availableViewTypes.length > 0) {
			viewer.changeViewType(availableViewTypes[0]);
		}
		viewer.setMapType(appState.mapType);

		//Wait for all data to be loaded before zooming to data
		await Promise.all(promises);
		updateCadAndAnnotationLayers();

		console.log("Loaded all data");
		viewer.zoomToFit(props.defaultZoomFactor);

		document.dispatchEvent(new Event("survey-loaded"));
		return true;
	} else {
		data.hasNoData = true;

		document.dispatchEvent(new Event("survey-loaded"));
		return false;
	}
}

function createLayers(survey: Survey){
	layers = [];

	if (!viewer) {
		return layers;
	}

	//Orthophoto layers
	for(var orthophoto of survey.orthophotos){
		layers.push({
			id: orthophoto.uuid,
			name: orthophoto.name,
			visible: true,
			layerType: LayerItemType.Orthophoto,
			layerStatus: LayerStatus.ProcessedAndLoaded
		});
	}

	//Pointclouds
	for (const pointcloud of survey.pointclouds) {
		layers.push({
			id: pointcloud.uuid,
			name: pointcloud.name,
			visible: true,
			layerType: LayerItemType.PointClouds,
			layerStatus: LayerStatus.Loading
		});
	}

	//Mesh layers
	for (const model of survey.models) {
		layers.push({
			id: model.uuid,
			name: model.name,
			visible: true,
			layerType: LayerItemType.Meshes,
			layerStatus: LayerStatus.Loading
		});
	}

	if (!props.hideVolumesAndProfiles) {
		//Volume layers  
		for (const volume of survey.volumes || []) {
			layers.push({
				id: volume.uuid,
				name: volume.name,
				visible: true,
				layerType: LayerItemType.Volumes,
				layerStatus: LayerStatus.Loading
			});
		}
	}

	//Add unprocessed files and files with errors
	for(const file of survey.inputFiles){
		if(file.status != SurveyInputDataStatusEnum.Processed){
			let layerType: LayerItemType | null = null;
			switch(file.type){
				case SurveyInputDataTypeEnum.Model:
					layerType = LayerItemType.Meshes;
					break;
				case SurveyInputDataTypeEnum.Cad:
					layerType = LayerItemType.Cad;
					break;
				case SurveyInputDataTypeEnum.Orthophoto:
					layerType = LayerItemType.Orthophoto;
					break;
				case SurveyInputDataTypeEnum.Pointcloud:
					layerType = LayerItemType.PointClouds;
					break;
				case SurveyInputDataTypeEnum.Profile:
					if (!props.hideVolumesAndProfiles) {
						layerType = LayerItemType.Profiles;
					}
					break;
				case SurveyInputDataTypeEnum.Volume:
					if (!props.hideVolumesAndProfiles) {
						layerType = LayerItemType.Volumes;
					}
					break;
			}

			if (!layerType) {
				continue;
			}

			let layerStatus: LayerStatus;
			switch(file.status){
				case SurveyInputDataStatusEnum.Processing:
				case SurveyInputDataStatusEnum.Waitingforprocessing:
					layerStatus = LayerStatus.Processing;
					break;
				case SurveyInputDataStatusEnum.Uploading:
					layerStatus = LayerStatus.Uploading;
					break;
				case SurveyInputDataStatusEnum.Processingfailed:
					layerStatus = LayerStatus.Error;
					break;
			}

			layers.push({
				id: file.uuid,
				name: file.name,
				visible: false,
				layerType: layerType,
				layerStatus: layerStatus
		});
		}
	}

	appState.setLayers(layers, props.viewIndex);
}

function updateCadAndAnnotationLayers() {
	//First remove all cad and annotation and profile layers
	const existingProfileLayers = layers.filter((l)=>l.layerType == LayerItemType.Profiles);
	layers = layers.filter((l)=>l.layerType != LayerItemType.Annotations && l.layerType != LayerItemType.Cad && l.layerType != LayerItemType.Profiles);

	//Cad items
	layers = layers.concat(viewer.cadLayers);

	//Profile items
	if (viewer.profileLayers.length > 0) {
		layers = layers.concat(viewer.profileLayers);
	} else {
		layers = layers.concat(existingProfileLayers);
	}

	//Activate first cad layer if active layer is null or if it no loger exists between layers
	if ((appState.activeCadLayer == null || viewer.cadLayers.find((l)=>l.name == appState.activeCadLayer?.name) == null) && viewer.cadLayers.length > 0) {
		appState.setActiveCadLayer(viewer.cadLayers[0])
	}

	//Add all annotations
	for (const i of viewer.annotations) {
		layers.push({
			id: i.uuid,
			name: i.title,
			visible: i.visible,
			layerType: LayerItemType.Annotations,
			layerStatus: LayerStatus.ProcessedAndLoaded
		});
	}

	appState.setLayers(layers, props.viewIndex);
}

function updateLayerObjectVisibility() {
	if (!viewer) {
		return;
	}
	let visibleLayersIds = appState.getViewLayers(props.viewIndex, LayerItemType.Meshes).filter((l) => l.visible).map((l) => l.id);
	for (const mesh of viewer.meshes) {
		const visible = visibleLayersIds.includes(mesh.uuid);
		if (mesh.visible == visible) {
			continue;
		}
		if (visible) {
			mesh.visible = true;
			viewer.meshesGroup.add(mesh as any);
		} else {
			mesh.visible = false;
			viewer.meshesGroup.remove(mesh as any);
		}
	}

	visibleLayersIds = appState.getViewLayers(props.viewIndex, LayerItemType.PointClouds).filter((l) => l.visible).map((l) => l.id);

	const pointclouds = viewer.pointclouds;
	for (const pc of pointclouds) {
		const visible = visibleLayersIds.includes(pc.uuid);
		if (pc.visible !== visible) {
			pc.visible = visible;
		}
	}

	visibleLayersIds = appState.getViewLayers(props.viewIndex, LayerItemType.Annotations).filter((l) => l.visible).map((l) => l.id);

	for (const annotation of viewer.annotations) {
		const visible = visibleLayersIds.includes(annotation.uuid);
		if (annotation.visible != visible) {
			annotation.visible = visible;
		}
	}

	visibleLayersIds = appState.getViewLayers(props.viewIndex, LayerItemType.Cad).filter((l) => l.visible).map((l) => l.name);

	for (const measurement of viewer.measurements) {
		const visible = visibleLayersIds.includes(measurement.layerName);
		if (measurement.visible != visible) {
			measurement.visible = visible;
		}
	}

	for (const unifiedLineMeasurement of viewer.unifiedLineMeasurements) {
		const visible = visibleLayersIds.includes(unifiedLineMeasurement.layerName);
		if (unifiedLineMeasurement.visible != visible) {
			unifiedLineMeasurement.visible = visible;
		}
	}

	visibleLayersIds = appState.getViewLayers(props.viewIndex, LayerItemType.Orthophoto).filter((l) => l.visible).map((l) => l.id);

	for (const mapView of viewer.orthophotoMaps) {
		const visible = visibleLayersIds.includes(mapView.uuid);
		if (mapView.visible != visible) {
			mapView.visible = visible;
		}
	}

	// Handle profile visibility with hierarchical structure only
    const visibleProfileIds = new Set<string>();
    
    const profileLayers = appState.getViewLayers(props.viewIndex, LayerItemType.Profiles);
    
    profileLayers.forEach(layer => {
        if (layer.children) {
            // Parent layer - check children visibility
            layer.children.forEach(child => {
                if (child.visible) {
                    visibleProfileIds.add(child.id);
                }
            });
        } else {
            // Simple layer without children
            if (layer.visible) {
                visibleProfileIds.add(layer.id);
            }
        }
    });

    // Update actual profile visibility
    for (const profile of viewer.profiles) {
        const visible = visibleProfileIds.has(profile.uuid) || 
                       visibleProfileIds.has(profile.userData?.layerName);
        
        if (profile.visible !== visible) {
            profile.visible = visible;
        }
    }

	visibleLayersIds = appState.getViewLayers(props.viewIndex, LayerItemType.Volumes).filter((l) => l.visible).map((l) => l.id);

	for (const volume of viewer.volumes) {
		const visible = visibleLayersIds.includes(volume.uuid);
		if (volume.visible != visible) {
			volume.visible = visible;
		}
	}
}

function onSelectedToolChanged() {
	const newTool = appState.selectedTool;
	viewer?.setTool(newTool);
}

function onViewTypeChanged() {
	const viewType: ViewType = appState.viewType;
	if (viewType !== ViewType.View3D) {
		// Restore the saved 3D view before camera mode/coordinate conversion.
		viewer?.externalImageManager.exit();
		closeExternalImage();
	}
	viewer?.changeViewType(viewType);
}

function deleteObject(object: any) {
	if (!object) {
		console.warn("Cannot delete object:", object);
		return;
	}
	viewer?.deleteObject(object);
}

function checkForCadLayerChanges() {
	if (viewer) {
		viewer.activeCadLayer = appState.activeCadLayer;
	}
	//When active layer changes the layer for selected object might be changed therefore
	//we need to update cad and annotation layers.
	updateCadAndAnnotationLayers();
}

function getViewer() {
	return viewer;
}

onMounted(() => {
	watch(() => appState.view1Survey?.inputFiles, (newFiles) => {
        const hasProjectsInProcessing = newFiles?.some(file => file.status === "processing" || file.status === "waitingforprocessing");
        if (hasProjectsInProcessing) {
            startInterval();
        } else {
            stopInterval();
        }
    }, { deep: true, immediate: true });

	if (data.wasCreated) {
		return;
	}
	data.wasCreated = true;

	//Disable right click context menu
	window.addEventListener("contextmenu", e => e.preventDefault());

	//State watchers
	watch(() => appState.selectedTool, onSelectedToolChanged);
	watch(() => appState.viewType, onViewTypeChanged);
	watch(() => appState.activeCadLayer, checkForCadLayerChanges);
	watch(() => appState.mapType, onMapTypeChanged);
	if (props.viewIndex == ViewIndex.View2) {
		watch(() => appState.view2Survey, onSurveyChanged);
		watch(() => appState.view2Layers, updateLayerObjectVisibility, {
			deep: true,
		});
	} else {
		watch(() => appState.view1Layers, updateLayerObjectVisibility, {
			deep: true,
		});
		watch(() => appState.view1Survey, onSurveyChanged);
	}

	viewer = new CustomViewer(potreeRenderArea.value);
	if (!props.fadeInput) {
		viewer.controls.fadeFactor = 0;
	}
	// console.log("Created PotreeVieweer budget:", props.pointBudgetMultiplier);
	(window as any).potreeViewer = this;
	viewer.onLayersChanged.addListener(updateCadAndAnnotationLayers);
	viewer.onToolCancel.addListener(onToolCancel);
	viewer.onSelectedElementChanged.addListener(onSelectedElementChanged);
	viewer.onExternalImageOpen.addListener((image) => {
		openExternalImage(image);
	});

	viewer.changeViewType(appState.viewType);
	viewer.setMapType(appState.mapType);

	//Load initial data
	onSurveyChanged();

	nextTick(() => {
		viewer?.setPointBudget(2000000 * props.pointBudgetMultiplier);
	})
});

onBeforeUnmount(() => {
	// console.log("Viewer unmount");
	stopInterval();
	viewer.cleanup();
});

//Expose methods on the outside of component 
//so that they can be called by other components
expose({
	getViewer,
	zoomToFit,
	zoomIn,
	zoomOut,
	deleteObject,
	copyViewFromViewer,
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      ref_key: "potreeRenderArea",
      ref: potreeRenderArea,
      class: "potree_render_area"
    }, null, 512),
    (props.showUi && data.isLoading)
      ? (_openBlock(), _createBlock(Loading, {
          key: 0,
          class: "project-loading"
        }))
      : _createCommentVNode("", true),
    (props.showUi && data.hasNoData)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _hoisted_4))
      : _createCommentVNode("", true),
    _hoisted_5,
    (data.externalViewerImage && _unref(viewer))
      ? (_openBlock(), _createBlock(ExternalImageControls, {
          key: 2,
          image: data.externalViewerImage,
          viewer: _unref(viewer),
          onClose: closeExternalImage,
          onSelect: _cache[0] || (_cache[0] = ($event: any) => (data.externalViewerImage = $event))
        }, null, 8, ["image", "viewer"]))
      : _createCommentVNode("", true),
    (props.showUi && _unref(appState).viewType == _unref(ViewType).View2D && _unref(appState).canChangeMapType)
      ? (_openBlock(), _createBlock(MapboxAttribution, { key: 3 }))
      : _createCommentVNode("", true)
  ]))
}
}

})